import React, { useEffect, useMemo, useState } from 'react';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import type { HDProduct, TicketTopic } from '../../../types/helpdeskSettingsInterfaces';
import { CATEGORY_SELECT_ITEMS } from '../../config/categories';

/*
 * Селекты «Продукт» и «Тип проблемы» для заявки. Используются в карточке
 * заявки, фильтрах списка и форме создания — один источник данных и один
 * вид группировки (продукты — по категории каталога, темы — по группе).
 */

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  CATEGORY_SELECT_ITEMS.map((c) => [c.value, c.label]),
);

export function useHdProducts(): HDProduct[] {
  const [items, setItems] = useState<HDProduct[]>([]);
  useEffect(() => {
    helpdeskSettingsApi
      .getProducts()
      .then((res) => setItems(res.data))
      .catch(() => setItems([]));
  }, []);
  return items;
}

export function useHdTopics(activeOnly = true): TicketTopic[] {
  const [items, setItems] = useState<TicketTopic[]>([]);
  useEffect(() => {
    helpdeskSettingsApi
      .getTicketTopics(activeOnly)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]));
  }, [activeOnly]);
  return items;
}

function groupBy<T>(items: T[], key: (t: T) => string): [string, T[]][] {
  const map = new Map<string, T[]>();
  items.forEach((it) => {
    const k = key(it);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(it);
  });
  return Array.from(map.entries());
}

interface SelectProps {
  value: number | '' | null | undefined;
  onChange: (id: number | null) => void;
  className?: string;
  /** Текст пустого варианта */
  emptyLabel?: string;
  /** Текущее значение, которого нет в списке (неактивная тема и т.п.) */
  current?: { id: number; name: string } | null;
  disabled?: boolean;
}

export const ProductSelect: React.FC<SelectProps & { products?: HDProduct[] }> = ({
  value,
  onChange,
  className,
  emptyLabel = '— не указан —',
  current,
  disabled,
  products,
}) => {
  const loaded = useHdProducts();
  const items = products ?? loaded;
  const groups = useMemo(
    () => groupBy(items, (p) => CATEGORY_LABEL[p.category] ?? 'Без категории'),
    [items],
  );
  const missing = current && !items.some((p) => p.id === current.id) ? current : null;
  return (
    <select
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      className={className}
    >
      <option value="">{emptyLabel}</option>
      {missing && <option value={missing.id}>{missing.name}</option>}
      {groups.map(([label, list]) => (
        <optgroup key={label} label={label}>
          {list.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
};

export const TopicSelect: React.FC<SelectProps & { topics?: TicketTopic[] }> = ({
  value,
  onChange,
  className,
  emptyLabel = '— не указан —',
  current,
  disabled,
  topics,
}) => {
  const loaded = useHdTopics();
  const items = topics ?? loaded;
  const groups = useMemo(() => groupBy(items, (t) => t.group || ''), [items]);
  const missing = current && !items.some((t) => t.id === current.id) ? current : null;
  return (
    <select
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      className={className}
    >
      <option value="">{emptyLabel}</option>
      {missing && <option value={missing.id}>{missing.name}</option>}
      {groups.map(([label, list]) =>
        label ? (
          <optgroup key={label} label={label}>
            {list.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </optgroup>
        ) : (
          list.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))
        ),
      )}
    </select>
  );
};
